import React, { ReactElement, useState } from "react";
import { Badge, Button, Icon, Notification, Heading, RadioGroup, Text } from "@telia/teddy";
import cn from "classnames";
import "./SubscriptionRadioCard.less";

interface Props {
    badge?: {
        backgroundColor: string;
        color: string;
        text: string;
    };
    discountBadge?: string;
    familyDiscount?: number;
    firstYearDiscountAmount?: number;
    footerButton?: React.ReactNode;
    hasSelectedSubscription?: boolean;
    isSelectable?: boolean;
    isSelected?: boolean;
    name?: string | ReactElement;
    offeringCode: string;
    price: string | number;
    priceStriketrough?: string | number;
    priceStriketroughSuffix?: string;
    priceSuffix?: string;
    ribbonText?: string;
    strikethroughTitle?: string;
    textBlocks: React.ReactNode;
    title?: string;
    isTeliaTrygg: boolean;
}

const SubscriptionRadioCard: React.FC<Props> = ({
    isTeliaTrygg,
    name,
    title,
    price,
    priceStriketrough,
    badge,
    discountBadge,
    ribbonText,
    textBlocks,
    priceSuffix = ",-/md.",
    footerButton,
    familyDiscount,
    isSelected,
    isSelectable = true,
    hasSelectedSubscription,
    strikethroughTitle,
    offeringCode,
}) => {
    const [openIncludedServices, setOpenIncludedServices] = useState(false);

    return (
        <div className={cn("subscriptionRadioCard", { "subscriptionRadioCard--selected": isSelected })}>
            {badge && (
                <Badge className={"subscriptionRadioCard__badge"} variant="attention">
                    {badge.text}
                </Badge>
            )}
            <RadioGroup.Item className="subscriptionRadioCard__radioGroupItem">
                <div className="subscriptionRadioCard__headingWrapper">
                    <RadioGroup.Label className="subscriptionRadioCard__radioGroupLabel">
                        <div className="subscriptionRadioCard__heading">
                            <div className="subscriptionRadioCard__content">
                                <div className={"subscriptionRadioCard__nameWrapper"}>
                                    {name && (
                                        <Heading
                                            mb="100"
                                            as="h2"
                                            variant="title-200"
                                            className={"subscriptionRadioCard__name"}
                                        >
                                            {strikethroughTitle && (
                                                <span className={"subscriptionRadioCard__strikethroughTitle"}>
                                                    {strikethroughTitle}
                                                </span>
                                            )}
                                            {name}
                                        </Heading>
                                    )}
                                </div>
                                {title && <span dangerouslySetInnerHTML={{ __html: title }} />}
                                <div className="subscriptionRadioCard__priceWrapper">
                                    {isTeliaTrygg && (
                                        <div className="subscriptionRadioCard__teliaTrygg">
                                            <Badge
                                                className="subscriptionRadioCard__priceBadge"
                                                variant="special"
                                                hideIcon
                                            >
                                                {price}
                                            </Badge>
                                            <Text className="subscriptionRadioCard__priceSuffix">{priceSuffix}</Text>
                                        </div>
                                    )}
                                    {!isTeliaTrygg && (
                                        <div>
                                            <Heading
                                                mb="100"
                                                as="h2"
                                                variant="title-200"
                                                title={`${price}`}
                                                className="subscriptionRadioCard__price"
                                            >
                                                {price}
                                                {priceSuffix}
                                                {priceStriketrough && (
                                                    <Text className="subscriptionRadioCard__priceStriketroughWrapper">
                                                        <div className="subscriptionRadioCard__priceStriketrough">
                                                            ord. pris: {priceStriketrough}
                                                        </div>
                                                    </Text>
                                                )}
                                            </Heading>
                                        </div>
                                    )}
                                </div>
                                {discountBadge && (
                                    <Badge className="subscriptionRadioCard__familyDiscount" variant="warning" hideIcon>
                                        {discountBadge}
                                    </Badge>
                                )}
                                {ribbonText && (
                                    <Badge className="subscriptionRadioCard__familyDiscount" variant="success" hideIcon>
                                        {ribbonText}
                                    </Badge>
                                )}
                                {!!familyDiscount && (
                                    <Badge className="subscriptionRadioCard__familyDiscount" variant="special" hideIcon>
                                        {familyDiscount},-/md i familierabatt
                                    </Badge>
                                )}
                                {!isSelectable && (
                                    <Notification mt="100" type="tile" variant="warning">
                                        <Notification.Icon />
                                        <Notification.Text>
                                            Dette abonnementet selges ikke lenger ved kjøp med rabattavtale.
                                            {!hasSelectedSubscription &&
                                                " For å gå videre i bestillingen må du endre til et nytt abonnement."}
                                        </Notification.Text>
                                    </Notification>
                                )}
                            </div>
                        </div>
                    </RadioGroup.Label>
                    <RadioGroup.Trigger
                        value={offeringCode}
                        disabled={!isSelectable}
                        className={"subscriptionRadioCard__radioGroupTrigger"}
                    />
                </div>
            </RadioGroup.Item>
            {!footerButton && <div className="subscriptionRadioCard__includedServices">{textBlocks}</div>}
            {footerButton && (
                <Button
                    onClick={() => setOpenIncludedServices(!openIncludedServices)}
                    className="subscriptionRadioCard__showIncluded"
                    variant="text"
                    size="sm"
                >
                    <span>Se detaljer</span>
                    <Icon
                        name={openIncludedServices ? "chevron-up" : "chevron-down"}
                        aria-hidden="true"
                        focusable="false"
                    />
                </Button>
            )}
            {openIncludedServices && <div className="subscriptionRadioCard__includedServices">{textBlocks}</div>}
        </div>
    );
};

export default SubscriptionRadioCard;
